import {
    NetworkError,
    generateRouteDefinition,
    makeCanonicalUrl,
} from '@jet-app/music-classical/utilities';
import {
    makeLibraryPlaylistsIntent,
    type LibraryPlaylistsIntent,
} from './LibraryPlaylistsIntent';
import type { IntentController } from '@jet/environment/dispatching';
import type { RouteProvider } from '@jet/environment/routing';
import type { ObjectGraph } from '@jet/environment/dependencies';
import { IntentKind } from '@jet-app/music-classical/intents';
import { requestViewPage } from '@jet-app/music-classical/api/request-view-page';
import type { ClassicalListViewPage } from '@jet-app/music-classical/api/types';
import { renderLibraryPlaylistsItems } from './renderLibraryPlaylistsItems';
import type {
    LibraryAllPlaylistsPage,
    LibraryPlaylistsItems,
} from '@jet-app/music-classical/models/pages/library/playlist';
import { renderAllPlaylistsPage } from './renderAllPlaylistsPage';
import {
    isEmptyPage,
    renderEmptyPage,
} from '@jet-app/music-classical/utilities/transforms/empty-view';
import { requiresSignIn } from '../utils/requires-sign-in';

const canonicalUrlRule = {
    path: '/library/all-playlists',
    query: [],
};

export const LibraryPlaylistsIntentController: IntentController<LibraryPlaylistsIntent> &
    RouteProvider = requiresSignIn({
    $intentKind: IntentKind.LIBRARY_PLAYLISTS,

    routes: generateRouteDefinition<LibraryPlaylistsIntent>(
        canonicalUrlRule,
        makeLibraryPlaylistsIntent,
        { isLibrary: true },
    ),

    async perform(
        intent: LibraryPlaylistsIntent,
        objectGraph: ObjectGraph,
    ): Promise<LibraryPlaylistsItems | LibraryAllPlaylistsPage> {
        const { forNavItems } = intent;
        const canonicalURL = makeCanonicalUrl(
            canonicalUrlRule,
            intent,
            objectGraph,
        );

        const apiResponse = await requestViewPage(
            `favorites/playlists`,
            objectGraph,
        );

        if (!apiResponse?.body) {
            throw NetworkError.noContent();
        }

        if (isEmptyPage(apiResponse)) {
            return renderEmptyPage<LibraryAllPlaylistsPage>(
                canonicalURL,
                apiResponse,
                objectGraph,
            );
        }

        const page = forNavItems
            ? renderLibraryPlaylistsItems(
                  apiResponse?.body as unknown as ClassicalListViewPage,
                  objectGraph,
              )
            : renderAllPlaylistsPage(canonicalURL, apiResponse, objectGraph);

        return page;
    },
});
